// src/utils/authorLookup.js
// One shared way to look up "who wrote this". Used by Home, Category and others.
// - Reads users/{uid} once per author per 5 minutes (even if 20 cards share an author)
// - Caches ONLY name + avatar path + the Top Minds flag (no email or other private fields)
import { doc, getDoc } from "firebase/firestore";
import { db } from "../firebase";
import { getCached } from "./simpleCache";
import { getAvatarUrl } from "./imageUrls";

const AUTHOR_TTL = 5 * 60 * 1000;

// Treats true / "yes" / "true" (any case) as on.
const isYes = (v) =>
  v === true || ["yes", "true"].includes(String(v ?? "").trim().toLowerCase());

/** Returns { name, avatarPath, topMinds } or null if the user can't be found. Never throws. */
export async function getAuthor(uid) {
  if (!uid) return null;
  try {
    return await getCached(
      `author-${uid}`,
      async () => {
        const snap = await getDoc(doc(db, "users", uid));
        if (!snap.exists()) return null;
        const d = snap.data();
        return {
          name: d.authorName || d.name || d.username || "",
          avatarPath: d.profileImage || d.profileImageUrl || d.photoURL || "",
          topMinds: isYes(d.topMindsAward),
        };
      },
      AUTHOR_TTL
    );
  } catch (e) {
    console.warn("Author lookup failed", e);
    return null;
  }
}

/** Public avatar URL for an author object from getAuthor(), or null */
export const authorAvatarUrl = (author) =>
  author && author.avatarPath ? getAvatarUrl(author.avatarPath) : null;

/** Name to store on a new idea. Never uses the email address. */
export async function resolveAuthorName(user) {
  const author = await getAuthor(user.uid);
  return (author && author.name) || user.displayName || "Anonymous";
}